// Global Types
import type {
  $Translate,
} from 'common.language';
import type {
  $FullRegattaNotification,
} from 'common.data';

// Global Enums
import {
  webPages,
  webSections,
} from 'common.enums';

// Global Components
import * as React from 'react';
import {
  NavLink,
} from 'react-router-dom';

// Helpers
import _ from 'lodash';
import moment from 'moment';
import ErrorLog from '@vroskus/library-sentry/dist/browser';
import {
  getDateTimeFormats,
  getRegattaNotificationContent,
} from 'common.helpers/dist/browser';
import {
  getLocalItemPath,
} from '../../../../helpers';

// Services
import Services from '../../../../services';

// Components
import Section from '../../../layout/Section';
import Loader from '../../../layout/Loader';
import NoData from '../../../common/NoData';

// Types
import type {
  $ReducerState,
} from '../../../../redux';

export type $OwnProps = $Translate;

export type $StateProps = {
  main: $ReducerState<'main'>;
};

export type $DispatchProps = Record<string, unknown>;

type $Props = $DispatchProps & $OwnProps & $StateProps;

type $State = {
  data: Array<$FullRegattaNotification>;
  filteredData: Array<$FullRegattaNotification>;
  loading: boolean;
};

const initialItemsQuantity = 5;

class RegattaNotificationsSectionView extends React.Component<$Props, $State> {
  constructor(props: $Props) {
    super(props);

    this.state = {
      data: [],
      filteredData: [],
      loading: false,
    };
  }

  componentDidMount(): void {
    this.getData();
    this.setupSocket();
  }

  setStateAsync(state: Partial<$State>): Promise<void> {
    return new Promise((resolve) => {
      this.setState(
        (pervState: $State) => ({
          ...pervState,
          ...state,
        }),
        resolve,
      );
    });
  }

  async getData(): Promise<void> {
    const {
      filteredData,
    } = this.state;

    try {
      await this.setStateAsync({
        loading: true,
      });

      const data = await Services.Api.getProfileRegattaNotificationsSectionData(undefined);

      await this.setStateAsync({
        data,
        filteredData: _.take(
          data,
          filteredData.length ? filteredData.length : initialItemsQuantity,
        ),
        loading: false,
      });
    } catch (error) {
      await this.setStateAsync({
        loading: false,
      });

      ErrorLog.exception(error as Error);
    }
  }

  setupSocket(): void {
    Services.Socket.on({
      action: (data) => {
        if (data.modelName === 'RegattaNotification') {
          this.getData();
        }
      },
      type: Services.Socket.type.modelChangedEvent,
    });
  }

  renderData() {
    const {
      main,
      t,
    } = this.props;
    const {
      filteredData,
    } = this.state;

    const formats = getDateTimeFormats({
      RegionId: main.RegionId,
      regions: main.regions,
    });

    const dateTimeFormat = `${formats.Date} ${formats.Time}`;

    return filteredData.map((dataItem: $FullRegattaNotification) => {
      const {
        body,
        title,
      } = getRegattaNotificationContent({
        regattaNotification: dataItem,
        t,
      });

      const now: moment.Moment = moment();
      const createdAtMoment: moment.Moment = moment(dataItem.createdAt);
      const daysToOutdate = 1;

      const isOutdated: boolean = now.subtract(
        daysToOutdate,
        'day',
      ).isAfter(moment(dataItem.createdAt));

      const path: string = getLocalItemPath({
        basePath: '/regatta',
        item: dataItem.Regatta,
        regions: main.regions,
      });

      return (
        <div
          className={`notification alert alert_outlined alert_secondary mb-2 flex flex-row items-center ${isOutdated === true ? 'outdated' : ''}`}
          key={dataItem.id}
        >
          <div className={'flex flex-row items-center justify-content-between grow'}>
            <div className={'grow'}>
              <strong className={'uppercase text-color-standard'}>
                {title}
              </strong>
              <div>{body}</div>
            </div>
            <div className={'ml-3 text-center whitespace-nowrap'}>
              <div
                className={'text-color-standard'}
                title={createdAtMoment.format(dateTimeFormat)}
              >
                {createdAtMoment.fromNow()}
              </div>
              <small>{createdAtMoment.format(dateTimeFormat)}</small>
            </div>
          </div>
          <div className={'ml-3 md:ml-5'}>
            <NavLink
              className={'btn btn_primary btn_outlined uppercase whitespace-nowrap px-2 md:px-3 py-1'}
              end
              title={t('Regatta page')}
              to={path}
            >
              <em className={'la la-flag text-2xl'} />
              <span className={'ml-2 hidden md:block'}>
                {t('Regatta page')}
              </span>
            </NavLink>
          </div>
        </div>
      );
    });
  }

  render() {
    const {
      t,
    } = this.props;
    const {
      data,
      filteredData,
      loading,
    } = this.state;

    return (
      <Section
        id={webSections[webPages.profilePage].notifications}
        title={t('Notifications')}
      >
        <Loader
          fade
          loading={loading === true}
        >
          {data.length > 0 ? (
            <>
              {this.renderData()}
              {filteredData.length !== data.length && (
                <div className={'text-center py-1'}>
                  <button
                    className={'btn btn_light btn_outlined uppercase whitespace-nowrap py-1'}
                    onClick={() => {
                      this.setState({
                        filteredData: _.take(
                          data,
                          filteredData.length + initialItemsQuantity,
                        ),
                      });
                    }}
                    type={'button'}
                  >
                    {t('Show older notifications')}
                  </button>
                </div>
              )}
            </>
          ) : (
            <NoData label={t('The list is empty')} />
          )}
        </Loader>
      </Section>
    );
  }
}

export default RegattaNotificationsSectionView;
